import { ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, afterNextRender, inject, signal, computed } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { email, FieldTree, form, minLength, required } from '@angular/forms/signals'
import { MatChipInputEvent } from '@angular/material/chips'
import { MatDialog } from '@angular/material/dialog'
import { MatInput } from '@angular/material/input'
import { ActivatedRoute, Router } from '@angular/router'

import { LaskuService } from '../../_angular/service/lasku/lasku.service'
import { arvonMuutokset, asetaArvo, nollaa } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'
import { Maa, MaaService } from '../../_jaettu-angular/service/maa.service'

import { LaskunAsiakas, LaskunLahetystapa } from '@lemon/core-firebase/model/lasku'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { SahkoisenLaskunValittajaService } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'

import { AsiakasPoistoVarmistusDialog, AsiakasPoistoVarmistusDialogData } from './asiakas-poisto-varmistus.dialog'

import { Verkkolaskuoperaattori } from '@lemon/core-firebase/service/lasku/sahkoisen-laskun-valittaja.service'
import { Observable, Subject } from 'rxjs'
import { map, startWith, takeUntil } from 'rxjs/operators'

interface AsiakasLomake {
  nimi: string
  ytunnus: string
  katuosoite: string
  postitoimipaikka: string
  postinro: string
  maa: string | null
  lisaavastaanottaja: string
  elaskuosoite: string
  elaskuvalittaja: string | Verkkolaskuoperaattori | null
  oletusLahetystapa: LaskunLahetystapa | null
}

const TYHJA_LOMAKE: AsiakasLomake = {
  nimi: '',
  ytunnus: '',
  katuosoite: '',
  postitoimipaikka: '',
  postinro: '',
  maa: null,
  lisaavastaanottaja: '',
  elaskuosoite: '',
  elaskuvalittaja: null,
  oletusLahetystapa: null,
}

@Component({
  selector: 'app-asiakas',
  templateUrl: './asiakas.component.html',
  styleUrls: ['./asiakas.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakasComponent implements OnInit, OnDestroy {
  @ViewChild(MatInput, { static: true }) nimiInput: MatInput

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)

  lomake = form(signal<AsiakasLomake>(TYHJA_LOMAKE), polku => {
    required(polku.nimi)
    minLength(polku.nimi, 2)
    required(polku.katuosoite)
    required(polku.postitoimipaikka)
    required(polku.postinro)
    required(polku.maa)
    email(polku.lisaavastaanottaja, { when: ({ value }) => !!value()?.trim() })
  })
  private readonly _kieli = toSignal(inject(LemonTranslationService).currentLanguageObservable)
  readonly fieldErrors = computed(() => {
    // Virheviestit käännetään nykyisellä kielellä, joten kielen vaihto muodostaa ne uudelleen
    this._kieli()
    return this.validationService.updateValidationStatus('asiakas', this.lomake)
  })

  readonly maat = signal<Maa[]>([])
  asiakas: LaskunAsiakas = null
  namename: string = 'assf' + Math.random()
  verkkolaskuoperaattorit: Observable<Verkkolaskuoperaattori[]>

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private laskuService: LaskuService,
    private laskuKopioija: LaskuKopioija,
    private validationService: FormValidationService,
    private errorHandler: ErrorHandler,
    private maaService: MaaService,
    private ladataanService: LadataanService,
    private _changeDetectorRef: ChangeDetectorRef,
    private sahkoisenLaskunValittajaService: SahkoisenLaskunValittajaService,
  ) {
    this.asiakas = this.laskuKopioija.annaUusiAsiakas()
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  ngOnInit() {
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    this.verkkolaskuoperaattorit = muutokset(this.lomake.elaskuvalittaja).pipe(
      startWith(''),
      map(value => this._filter(value)),
    )

    // Bind to changes
    muutokset(this.lomake.nimi).subscribe(value => {
      this.asiakas.nimi = this.validationService.processValue(value)
    })
    muutokset(this.lomake.ytunnus).subscribe(value => {
      this.asiakas.ytunnus = this.validationService.processValue(value)
    })
    muutokset(this.lomake.katuosoite).subscribe(value => {
      this.asiakas.katuosoite = this.validationService.processValue(value)
    })
    muutokset(this.lomake.postitoimipaikka).subscribe(value => {
      this.asiakas.postitmp = this.validationService.processValue(value)
    })
    muutokset(this.lomake.postinro).subscribe(value => {
      this.asiakas.postinro = this.validationService.processValue(value)
    })
    muutokset(this.lomake.maa).subscribe(value => {
      this.asiakas.maa = this.validationService.processValue(value)
    })
    muutokset(this.lomake.lisaavastaanottaja).subscribe(value => {
      if (value && (value.indexOf(',') !== -1 || value.indexOf(' ') !== -1)) {
        this.lisaaEmail(this.lomake.lisaavastaanottaja)
      }
    })

    muutokset(this.lomake.elaskuosoite).subscribe(value => {
      this.asiakas.sahkoinenosoite.sahkoinenOsoite = this.validationService.processValue(value)
    })
    muutokset(this.lomake.oletusLahetystapa).subscribe((value: LaskunLahetystapa | null) => {
      this.asiakas.oletusLahetystapa = value ?? undefined
    })
    muutokset(this.lomake.elaskuvalittaja).subscribe(value => {
      let val = null
      if (value) {
        if (value instanceof String || typeof value === 'string') {
          val = value
        } else if ((value as Verkkolaskuoperaattori).tunnus) {
          val = (value as Verkkolaskuoperaattori).tunnus
        }
      }
      // console.log('Asetetaan valittaja', val)
      this.asiakas.sahkoinenosoite.sahkoinenValittaja = val
    })

    this.maaService.kaikkiMaatJaVeroalueetObservable.pipe(takeUntil(this.ngUnsubscribe)).subscribe(maat => {
      this.maat.set(maat)
    })

    this.route.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe((data: { asiakas: LaskunAsiakas }) => {
      nollaa(this.lomake, TYHJA_LOMAKE)
      this.asiakas = this.laskuKopioija.copyAsiakas(data.asiakas)
      if (!this.asiakas.sahkoinenosoite) {
        this.asiakas.sahkoinenosoite = {
          sahkoinenOsoite: null,
          sahkoinenValittaja: null,
        }
      }
      this.alustaLomakkeenTiedot(this.asiakas)
      this._changeDetectorRef.markForCheck()
    })

    // Signal Forms asettaa kentän käytöstilan ensimmäisessä muutostunnistuksessa, mikä nollaa Materialin kentän kohdistustilan
    afterNextRender(() => this.nimiInput.focus(), { injector: this._injector })
  }

  private alustaLomakkeenTiedot(asiakas: LaskunAsiakas) {
    asetaArvo(this.lomake.nimi, asiakas.nimi ?? '')
    asetaArvo(this.lomake.ytunnus, asiakas.ytunnus ?? '')
    asetaArvo(this.lomake.katuosoite, asiakas.katuosoite ?? '')
    asetaArvo(this.lomake.postitoimipaikka, asiakas.postitmp ?? '')
    asetaArvo(this.lomake.postinro, asiakas.postinro ?? '')
    asetaArvo(this.lomake.maa, asiakas.maa)
    asetaArvo(this.lomake.oletusLahetystapa, asiakas.oletusLahetystapa ?? null)

    if (asiakas.sahkoinenosoite) {
      const valittajaTunnus = asiakas.sahkoinenosoite.sahkoinenValittaja
      const haettuValittaja: Verkkolaskuoperaattori = this.sahkoisenLaskunValittajaService.annaValittaja(valittajaTunnus)
      asetaArvo(this.lomake.elaskuvalittaja, haettuValittaja || valittajaTunnus || null)
      asetaArvo(this.lomake.elaskuosoite, asiakas.sahkoinenosoite.sahkoinenOsoite ?? '')
    } else {
      asetaArvo(this.lomake.elaskuosoite, '')
      asetaArvo(this.lomake.elaskuvalittaja, null)
    }
  }

  private _filter(value: string | Verkkolaskuoperaattori): Verkkolaskuoperaattori[] {
    if (value === '' || !value) {
      return []
    }
    let filterValue = ''
    if (value instanceof String || typeof value === 'string') {
      filterValue = value.toLowerCase()
    } else {
      filterValue = value && value.nimi ? value.nimi.toLowerCase() : ''
    }
    return this.sahkoisenLaskunValittajaService
      .annaKaikkiValittajat()
      .filter(operaattori => operaattori.nimi.toLowerCase().includes(filterValue) || operaattori.tunnus.toLowerCase().includes(filterValue))
  }

  valittajaDisplayFn(operaattori?: Verkkolaskuoperaattori): string | undefined {
    if (operaattori && operaattori.nimi) {
      return operaattori.nimi + ' (' + operaattori.tunnus + ')'
    }
    if (operaattori instanceof String || typeof operaattori === 'string') {
      return operaattori as any as string
    }
    return undefined
  }

  save() {
    if (!this.lomake().valid()) {
      this.validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    this.ladataanService.aloitaLataaminen()
    this.laskuService
      .updateAsiakkaat([this.asiakas])
      .then(result => {
        this._changeDetectorRef.markForCheck()
        this.ladataanService.lopetaLataaminen()
        this.router.navigate(['/laskutus/asiakkaat'])
      })
      .catch(err => {
        this.ladataanService.lopetaLataaminen()
        this.errorHandler.handleError(err)
      })
  }

  peruuta() {
    this.router.navigate(['/laskutus/asiakkaat'])
  }

  poista() {
    const data: AsiakasPoistoVarmistusDialogData = {
      asiakas: this.asiakas,
    }
    this.dialog
      .open(AsiakasPoistoVarmistusDialog, { data: data })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.router.navigate(['/laskutus/asiakkaat'])
        }
      })
  }

  lisaa() {
    this.lisaaEmail(this.lomake.lisaavastaanottaja)
  }

  private lisaaEmail(field: FieldTree<string>) {
    if (!field().valid()) {
      return
    }
    const value = field().value()
    if ((value || '').trim()) {
      let arvo = this.validationService.processValue(value.trim())
      arvo = arvo.replace(/,/g, '').trim()
      if (!this.asiakas.laskunVastaanottajat) {
        this.asiakas.laskunVastaanottajat = []
      }
      this.asiakas.laskunVastaanottajat.push(arvo)
    }
    asetaArvo(field, '')
    this._changeDetectorRef.markForCheck()
  }

  lisaaAsiakas(event: MatChipInputEvent): void {
    this.lisaa()
  }

  poistaAsiakas(vastaanottaja: string): void {
    const index = this.asiakas.laskunVastaanottajat.indexOf(vastaanottaja)
    if (index >= 0) {
      this.asiakas.laskunVastaanottajat.splice(index, 1)
    }
  }
}
